import React, { useState } from "react";
import {
  SHOW_LESS_LABEL,
  SHOW_MORE_LABEL,
} from "../../../constants/Dictionary";
import { theme } from "../../../constants/StyledComponents";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { BodyTyp } from "../../Typography/Typography";
import { IconManager } from "../IconManager/IconManager";
import { Link, LinkSizeEnum } from "../Link/Link";
import {
  StyledAppNotification,
  StyledAppNotificationIcon,
  StyledAppNotificationContent,
} from "./AppNotification.styled";
import { ReactComponent as CareerManagerIcon } from "../../../icons/careerManager.svg";
import { ReactComponent as GraduationIcon } from "../../../icons/graduation.svg";
import { ReactComponent as WelcomeIcon } from "../../../icons/welcome.svg";
import { ReactComponent as SettingsIcon } from "../../../icons/manage.svg";
import { ReactComponent as CVIcon } from "../../../icons/resume.svg";
import {
  Notification,
  NotificationCategoryEnum,
} from "../../../types/api/notifications.type";
import { timeDifference } from "../../../utils/date/date.utils";
import { UnReadMark } from "../../UnReadMark/UnReadMark";

export const getAppNotificationIcon = (
  type: NotificationCategoryEnum,
): React.ReactNode => {
  switch (type) {
    case NotificationCategoryEnum.CM:
      return <CareerManagerIcon />;
    case NotificationCategoryEnum.CM_SHEET:
      return <GraduationIcon />;
    case NotificationCategoryEnum.INFO:
      return <WelcomeIcon />;
    case NotificationCategoryEnum.CV:
      return <CVIcon />;
    case NotificationCategoryEnum.SYS:
      return <SettingsIcon />;
  }
};

export interface AppNotificationProps {
  notification: Notification;
  onRead: () => void;
}

export const AppNotification = ({
  notification: item,
  onRead,
}: AppNotificationProps) => {
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const [isRead, setIsRead] = useState(item.read);

  const markAsRead = async () => {
    if (isRead) {
      return;
    }

    setIsRead(true);
    onRead();
  };

  const toggleExpansion = () => {
    setExpanded(!isExpanded);
  };

  const maxCharacters = 48;

  const bodyIsMoreMaxCharacters = () => {
    return item.body.length >= maxCharacters;
  };

  return (
    <StyledAppNotification
      $read={isRead}
      $expanded={isExpanded}
      onClick={markAsRead}
    >
      <StyledAppNotificationIcon>
        <IconManager size={IconSizeEnum.SMALL}>
          {getAppNotificationIcon(item.category)}
        </IconManager>
      </StyledAppNotificationIcon>
      <StyledAppNotificationContent>
        <div className="notification-content-header">
          <BodyTyp fontWeight={FontWeightEnum.SEMIBOLD}>{item.title}</BodyTyp>
          <div className="notification-content-header-badge">
            <BodyTyp variant="3" color={theme.colors.grey_2}>
              {timeDifference(new Date(), new Date(item.created_at))}
            </BodyTyp>
            <UnReadMark visible={!isRead} />
          </div>
        </div>
        <div className="notification-content-description">
          <BodyTyp>
            {isExpanded
              ? item.body
              : bodyIsMoreMaxCharacters()
                ? `${item.body.substring(0, maxCharacters)}...`
                : item.body}
          </BodyTyp>
        </div>
        {bodyIsMoreMaxCharacters() && (
          <Link onClick={toggleExpansion} size={LinkSizeEnum.SMALL}>
            <BodyTyp variant="3" fontWeight={FontWeightEnum.SEMIBOLD}>
              {isExpanded ? SHOW_LESS_LABEL : SHOW_MORE_LABEL}
            </BodyTyp>
          </Link>
        )}
      </StyledAppNotificationContent>
    </StyledAppNotification>
  );
};
